import axios, { AxiosRequestConfig } from 'axios';
import { auth } from '../firebaseAuth';
import { getAuthHeaders } from './authHeaders';
import { resolveFrontendApiBase } from './apiBase';

const financialPaths = new Set(['/renew-subscription', '/reset-subscription-cycle', '/purchase-forever-credits-direct', '/apply-retention-coupon']);

async function configFor(url: string, config?: AxiosRequestConfig, payload?: unknown) {
  const target = new URL(url, window.location.origin);
  const api = new URL(resolveFrontendApiBase(), window.location.origin);
  if (target.origin !== api.origin) throw new Error('Account requests require the configured API origin.');
  const headers = auth.currentUser ? await getAuthHeaders() : {};
  let operationStorageKey: string | undefined;
  let operationId: string | undefined;
  if (financialPaths.has(target.pathname)) {
    if (!auth.currentUser) throw new Error('Sign in to manage billing.');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)));
    const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    operationStorageKey = `billing-operation:${auth.currentUser.uid}:${target.pathname}:${fingerprint}`;
    operationId = sessionStorage.getItem(operationStorageKey) || crypto.randomUUID();
    // Fail before sending if durable browser retry state cannot be recorded.
    sessionStorage.setItem(operationStorageKey, operationId);
  }
  return {
    config: { ...config, headers: { ...config?.headers, ...headers, ...(operationId ? { 'Idempotency-Key': operationId } : {}) } },
    operationStorageKey,
  };
}

/** Explicit account callers retain the existing Axios auth recovery interceptor. */
const accountApi = {
  ...axios,
  get: async <T = any>(url: string, config?: AxiosRequestConfig) => axios.get<T>(url, (await configFor(url, config)).config),
  post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const prepared = await configFor(url, config, data);
    const response = await axios.post<T>(url, data, prepared.config);
    if (prepared.operationStorageKey && response.status !== 202 && !(response.data as any)?.pending) {
      sessionStorage.removeItem(prepared.operationStorageKey);
    }
    if (new URL(url, window.location.origin).pathname === '/confirm-forever-credits-payment' && auth.currentUser) {
      const prefix = `billing-operation:${auth.currentUser.uid}:/purchase-forever-credits-direct:`;
      Object.keys(sessionStorage).filter(key => key.startsWith(prefix)).forEach(key => sessionStorage.removeItem(key));
    }
    return response;
  },
};

export default accountApi;
